import { Field } from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';

import { Button } from '@/shared';

import { Hint } from './hint';
import { Label } from './label';

export type ButtonGroupOption = {
	label: React.ReactNode;
	value: string;
	subLabel?: string;
};

export type ButtonGroupProps = {
	name: string;
	options: ButtonGroupOption[];
	value?: string;
	onChange: (value: string) => void;
	label?: string;
	hint?: string;
	disabled?: boolean;
	invalid?: boolean;
	fullWidth?: boolean;
	className?: string;
};

export const ButtonGroup = ({
	name,
	options,
	value = '',
	onChange,
	label,
	hint,
	disabled = false,
	invalid = false,
	fullWidth = true,
	className,
}: ButtonGroupProps) => {
	const handleClick = (optionValue: string) => {
		if (!disabled) {
			onChange(optionValue);
		}
	};

	return (
		<Field className="relative">
			{label && (
				<div className="mb-2">
					<Label>{label}</Label>
				</div>
			)}
			<div
				className={clsx(
					'flex flex-col gap-2 md:flex-row',
					fullWidth && 'w-full',
					className,
				)}
			>
				<input type="hidden" name={name} value={value} />
				{options.map((option) => (
					<Button
						key={option.value}
						type="button"
						variant="outline"
						size="44"
						className={clsx(
							'flex h-auto min-h-11 flex-col items-center justify-center py-2 transition-all',
							value === option.value && 'font-medium ring-2 !ring-gray-1',
							invalid && 'ring-2 ring-red',
							fullWidth && 'flex-1',
						)}
						disabled={disabled}
						onClick={() => handleClick(option.value)}
					>
						<span className="block font-medium leading-tight">
							{option.label}
						</span>
						{option.subLabel && (
							<span
								className={clsx(
									'mt-0.5 block text-xs text-gray-3 transition-colors',
									option.value === value && 'font-medium',
								)}
							>
								{option.subLabel}
							</span>
						)}
					</Button>
				))}
			</div>
			{hint && <Hint>{hint}</Hint>}
		</Field>
	);
};
